import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { fetchCustomObjectInfo } from "./customObjectInfoCardFunctions";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import CustomObjectInfoCardSection from "./CustomObjectInfoCardSection";
import { Loader } from "../../../widgets/loaders/Loader";
import { authorizeToCustomRest } from "../../../features/customAuth/customAuth";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import _ from "lodash";
import { upgradedDeepGet } from "../../../features/dataFunctions/dataFunctions";
import { useDispatch } from "react-redux";
import { setCustomObjectDocuments } from "../../../stateManagement/reducers";
import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import { getAllRoadObjectsLayer, getWKTFeatureVectorLayer } from "../../../widgets/map/mapFeatures/layers";
import { countExtentFromWKTFeatures } from "../../../widgets/map/mapFeatures/helpers";
import { formatObject } from "../../../features/dataFormatFunctions/formatObject";

export function CustomObjectInfoCard() {
    const dispatch = useDispatch()
    const { id, objectType, entityId }: any = useParams()
    const [name, setName] = useState<any>(null)
    const [objectInfo, setObjectInfo] = useState<any>(null)
    const [cardStructure, setCardStructure] = useState<any>(null)
    const [objectInfoIsFetching, setObjectInfoIsFetching] = useState<boolean>(true)
    const [mapIsSown, setMapIsShown] = useState<boolean>(false)
    const [additionalLayers, setAdditionalLayers] = useState<any>([])
    const [fitExtent, setFitExtent] = useState<any>();
    const [fitExtentPadding, setFitExtentPadding] = useState<any>([]);
    const [permanentLayersMap] = useState([getAllRoadObjectsLayer()])
    const restAuthToken = useAppSelector(state => state.customAuth.customAuthToken)
    const currentConfig = getItemWebStorage('currentConfig')
    const { cardAdditionalItems } = currentConfig
    const cardAdditionalItem = cardAdditionalItems.find((item: any) => item?.id === id)

    useEffect(() => {
        const { title, structure, restAuthRequestOptions } = cardAdditionalItem ?? {}
        if (restAuthRequestOptions && !restAuthToken) {
            authorizeToCustomRest(restAuthRequestOptions)
        }
        if (title) {
            setName(title)
        }
        if (structure) {
            setCardStructure(structure)
        }
    }, [objectType])

    useEffect(() => {
        if (restAuthToken) {
            getCustomObjectInfo()
        }
    }, [restAuthToken, objectType])

    async function getCustomObjectInfo() {
        setObjectInfoIsFetching(true)
        const { restAuthRequestOptions, api, documents: documentsOptions } = cardAdditionalItem ?? {}
        let { objectInfo: objectInfoApi } = api ?? {}
        let objectInfo = await fetchCustomObjectInfo(entityId, objectInfoApi, restAuthRequestOptions, restAuthToken)
        const { nestedApis } = objectInfoApi ?? {}
        if (nestedApis) {
            for (const nestedApiName in nestedApis) {
                const nestedApi = nestedApis[nestedApiName]
                const parentPropertyForEntityId = nestedApi?.parentPropertyForEntityId
                const nestedEntityId = upgradedDeepGet(objectInfo, parentPropertyForEntityId)
                const nestedObjectInfo = await fetchCustomObjectInfo(nestedEntityId, nestedApi, restAuthRequestOptions, restAuthToken)
                objectInfo = { ...objectInfo, [`nestedApis_${nestedApiName}`]: nestedObjectInfo }
            }
        }
        const { available: documentAreAvailable, propertyForDocuments } = documentsOptions ?? {}
        if (documentAreAvailable && propertyForDocuments) {
            const documents = upgradedDeepGet(objectInfo, propertyForDocuments)
            dispatch(setCustomObjectDocuments(documents))
        }
        setObjectInfo(objectInfo)
        setObjectInfoIsFetching(false)
    }

    useEffect(() => {
        const { map } = cardAdditionalItem ?? {}
        if (map && objectInfo) {
            configureCardMap(map)
        }
    }, [objectType, objectInfo])

    function configureCardMap(mapOptions: any) {
        const { visible, geometry: geometryOptions } = mapOptions ?? {}
        if (visible && geometryOptions) {
            const { propertyForGeometryType, valueForPointGeometryType, valueForLineGeometryType, propertyForPointGeometry, propertyForLineGeometry } = geometryOptions ?? {}
            const geometryType = objectInfo?.[propertyForGeometryType]
            let objectWKTGeometry
            if (geometryType === valueForPointGeometryType) {
                objectWKTGeometry = objectInfo?.[propertyForPointGeometry]
            } else if (geometryType === valueForLineGeometryType) {
                objectWKTGeometry = objectInfo?.[propertyForLineGeometry]
            }
            if (objectWKTGeometry) {
                const objectLayer = getWKTFeatureVectorLayer(objectWKTGeometry)
                setAdditionalLayers([objectLayer])
                const extent = countExtentFromWKTFeatures(objectWKTGeometry)
                setFitExtent(extent)
                setFitExtentPadding([50, 50, 50, 50])
                setMapIsShown(true)
            }
        }
    }

    return (
        <>
            <ContentHeader title={name} />
            <div className="custom-object-info-page-div">
                {objectInfoIsFetching
                    ?
                    <Loader />
                    :
                    <div className="object-info-div" >
                        {objectInfo && cardStructure && cardStructure.map((item: any) => (
                            <CustomObjectInfoCardSection
                                sectionName={item?.sectionName}
                                sectionFields={item?.fields}
                                sectionButtons={item?.buttons}
                                objectInfo={formatObject(objectInfo)}
                            />
                        ))}
                        {mapIsSown &&
                            <div className="map-widget-table-object-info">
                                <MapWidget
                                    additionalLayers={additionalLayers}
                                    permanentLayers={permanentLayersMap}
                                    propsFitExtent={fitExtent}
                                    propsFitExtentPadding={fitExtentPadding}
                                    // mapEventHandlers={mapEventHandlers}
                                    mapInfoWidgetIsShown={false}
                                    mapType="card"
                                />
                            </div>
                        }
                    </div>
                }
            </div>
        </>
    )
}
